import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { ReactNode } from 'react';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom } from 'src/components/table';
import Image from '../../../../components/image';
import { translateKey } from 'src/locales/translate';
import { getFileUrl } from 'src/utils/getFileUrl';
import { textNotePreview } from 'src/sections/@dashboard/profile/utils/textNotePreview';
import {
  TAB_TABLE_CONTAINER_SX,
  TAB_TABLE_PAGINATION_SX,
  TAB_TABLE_ROWS_PER_PAGE_OPTIONS,
  TAB_TABLE_SCROLL_BODY_SX,
} from 'src/theme/tableContainer';

interface Row {
  id: string;
  name: string;
  url: string;
  originalFilename?: string;
  description?: string;
  date: string;
  visitId?: string;
  canDelete?: boolean;
  documentType?: string;
  content?: string;
}

interface Column {
  id: keyof Row | 'actions';
  label: string;
  minWidth?: number;
  align?: 'left' | 'center';
  render?: (row: Row) => ReactNode;
}

//TODO: Fix type
export type ProfileFilesTableProps = {
  data: any[];
  onDelete?: (filename: string) => void;
  isDeleting?: boolean;
  profileId?: string;
  onEditTextNote?: (note: { filename: string; content: string }) => void;
  /**
   * Paging is driven from above: the rows come a page at a time from the
   * server, so the component that owns the query owns the page state too.
   * `total` is every document, not the ones in `data`.
   */
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const getFileIcon = (name: string) => {
  const ext = name.split('.').slice(-1)[0];

  if (ext === 'pdf') {
    return <Iconify icon="bxs:file-pdf" width="24px" color="error.dark" />;
  }

  if (ext === 'jpg' || ext === 'png' || ext === 'jpeg') {
    return <Iconify icon="bxs:file-image" width="24px" color="primary.main" />;
  }

  return <Iconify icon="bxs:file-blank" width="24px" />;
};

export default function ProfileFilesTable({
  data,
  onDelete,
  isDeleting,
  profileId,
  onEditTextNote,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
}: ProfileFilesTableProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleOnRowClick = async (url: string, event: React.MouseEvent) => {
    // Don't open if clicking on delete button
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }

    // Check if URL is a full URL or just a filename
    const isFullUrl = url.startsWith('http');

    if (isFullUrl) {
      // Public file - open directly
      window.open(url, '_blank');
    } else {
      // Private file - get signed URL first
      try {
        const signedUrl = profileId
          ? await getFileUrl({ kind: 'profileFile', profileId, filename: url })
          : url;
        window.open(signedUrl, '_blank');
      } catch (error) {
        console.error('Error fetching signed URL:', error);
        enqueueSnackbar(translateKey('petAi.fileOpenError'), {
          variant: 'error',
        });
      }
    }
  };

  const tableData: Row[] = data.map((document) => ({
    id: document.id,
    name: document.filename,
    url: document.url,
    originalFilename: document.originalFilename,
    description: document.description,
    date: document.uploadedAt || document.createdAt || new Date().toISOString(),
    visitId: document.visitId,
    canDelete: document.canDelete,
    documentType: document.documentType,
    content: document.content,
  }));

  // No sorting or slicing here: the server returns this page, already newest
  // first. Re-sorting would only reorder the rows of the current page.

  const COLUMNS: Column[] = [
    {
      id: 'name',
      label: 'Nazwa pliku',
      render: (value: Row) =>
        value.documentType === 'TEXT_NOTE' ? (
          <Box
            sx={{
              gap: theme.spacing(2),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Iconify
              icon="eva:file-text-outline"
              width="24px"
              color="primary.main"
            />
            <Typography variant="body2">
              {translateKey('hardcoded.notatka_tekstowa')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              gap: theme.spacing(2),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {getFileIcon(value.originalFilename || value.name)}
            <Typography variant="body2">
              {value.originalFilename || value.name.split('_')[1] || value.name}
            </Typography>
          </Box>
        ),
    },
    {
      id: 'description',
      label: translateKey('search.ui.aboutTab'),
      render: (row: Row) => {
        const desc =
          row.documentType === 'TEXT_NOTE'
            ? textNotePreview(row.content)
            : row.description || '';
        return (
          <Tooltip
            title={desc}
            arrow
            disableHoverListener={!desc || desc.length < 30}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {desc}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: 'date',
      label: 'Data dodania',
      render: (value: Row) =>
        format(new Date(value.date), 'dd/MM/yyyy HH:mm:ss'),
    },
    ...(onDelete
      ? [
          {
            id: 'actions' as const,
            label: translateKey('visit.historyTableActions'),
            align: 'center' as const,
            minWidth: 100,
            render: (row: Row) => {
              if (row.canDelete === false) return null;
              return (
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title={translateKey('hardcoded.usun_plik')}>
                    <IconButton
                      className="delete-button"
                      data-testid="file-delete-button"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(row.name);
                      }}
                      disabled={isDeleting}
                      size="small"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            },
          },
        ]
      : []),
  ];

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={TAB_TABLE_SCROLL_BODY_SX}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row) => (
              <TableRow
                hover
                key={row.id}
                data-testid="patient-file-row"
                data-file-name={row.name}
                onClick={(e) => {
                  if (row.documentType === 'TEXT_NOTE') {
                    if ((e.target as HTMLElement).closest('button')) return;
                    onEditTextNote?.({
                      filename: row.name,
                      content: row.content || '',
                    });
                  } else {
                    handleOnRowClick(row.url, e);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '& .delete-button': {
                    opacity: 0,
                  },
                  '&:hover .delete-button': {
                    opacity: 1,
                  },
                }}
              >
                {COLUMNS.map((column) => {
                  const value =
                    column.id !== 'actions'
                      ? row[column.id as keyof Row]
                      : null;
                  return (
                    <TableCell key={column.id} align={column.align}>
                      {column.render ? column.render(row) : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );
  return (
    <>
      <TableContainer sx={TAB_TABLE_CONTAINER_SX}>
        {tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty waiting room"
                src="/assets/images/empty_table.svg"
              />
              <Typography>{translateKey('hardcoded.brak_plikow')}</Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <TablePaginationCustom
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={TAB_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={TAB_TABLE_PAGINATION_SX}
      />
    </>
  );
}
